import { useEffect } from 'react';
import './FusionBoltAnimation.css';

/**
 * PROFESSIONAL FUSION BOLT ANIMATION
 * Electric-type special move with lightning fusion mechanics
 * 
 * Key Features:
 * - Electric-type special attack (100 base power, 100% accuracy)
 * - Signature move with unique fusion lightning mechanics
 * - Massive electrical discharge with fusion energy core
 * - Professional gaming company quality with dramatic timing
 * - Special move animation with fusion mechanics and electrical devastation
 */
export default function FusionBoltAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning - more robust detection
    const container = playerEl.closest('.battle-screen') || 
                     playerEl.closest('.battle-container') || 
                     document.querySelector('.battle-screen') || 
                     document.querySelector('.battle-container') || 
                     document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Helpers to keep elements inside container bounds
    const clampX = (x, margin = 0) => Math.min(Math.max(x, margin), Math.round(containerRect.width) - margin);
    const clampY = (y, margin = 0) => Math.min(Math.max(y, margin), Math.round(containerRect.height) - margin);

    // Calculate fusion bolt trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    // Elements to clean up
    const elements = [];

    // PHASE 1: Electrical Fusion Charging (0-800ms)
    console.log('Fusion Bolt Phase 1: Electrical Fusion Charging');
    
    // Player electrical fusion charging
    playerEl.classList.add('fusion-bolt-user-charging');
    
    // Fusion energy core at player (fades out in CSS)
    const fusionCore = document.createElement('div');
    fusionCore.className = 'fusion-bolt-fusion-core';
    fusionCore.style.left = `${playerX - 60}px`;
    fusionCore.style.top = `${playerY - 60}px`;
    fusionCore.style.width = '120px';
    fusionCore.style.height = '120px';
    container.appendChild(fusionCore);
    elements.push(fusionCore);

    // Electrical fusion particles around player
    for (let i = 0; i < 24; i++) {
      const fusionParticle = document.createElement('div');
      fusionParticle.className = 'fusion-bolt-fusion-particle';
      const angle = (i / 24) * 360;
      const radius = 70 + (i % 6) * 10;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      fusionParticle.style.left = `${particleX - 5}px`;
      fusionParticle.style.top = `${particleY - 5}px`;
      fusionParticle.style.animationDelay = `${i * 30}ms`;
      fusionParticle.textContent = '⚡';
      container.appendChild(fusionParticle);
      elements.push(fusionParticle);
    }

    // Fusion lightning rings
    for (let i = 0; i < 4; i++) {
      const fusionRing = document.createElement('div');
      fusionRing.className = 'fusion-bolt-fusion-ring';
      fusionRing.style.left = `${playerX - (80 + i * 20)}px`;
      fusionRing.style.top = `${playerY - (80 + i * 20)}px`;
      fusionRing.style.width = `${160 + i * 40}px`;
      fusionRing.style.height = `${160 + i * 40}px`;
      fusionRing.style.animationDelay = `${i * 150}ms`;
      container.appendChild(fusionRing);
      elements.push(fusionRing);
    }

    // PHASE 2: Fusion Bolt Launch (800-1200ms)
    const phase2Timer = setTimeout(() => {
      console.log('Fusion Bolt Phase 2: Fusion Bolt Launch');
      
      // Player fusion bolt launch
      playerEl.classList.remove('fusion-bolt-user-charging');
      playerEl.classList.add('fusion-bolt-user-launch');

      // Create the massive fusion bolt projectile
      const fusionBolt = document.createElement('div');
      fusionBolt.className = 'fusion-bolt-projectile';
      fusionBolt.style.left = `${clampX(playerX, 25) - 25}px`;
      fusionBolt.style.top = `${clampY(playerY, 25) - 25}px`;
      fusionBolt.style.width = '50px';
      fusionBolt.style.height = '50px';
      // IMPORTANT: translate by the delta, not absolute coordinates
      fusionBolt.style.setProperty('--target-x', `${dx}px`);
      fusionBolt.style.setProperty('--target-y', `${dy}px`);
      fusionBolt.style.setProperty('--distance', `${distance}px`);
      fusionBolt.style.transform = `rotate(${angle}deg)`;
      container.appendChild(fusionBolt);
      elements.push(fusionBolt);

      // Fusion bolt trail effects
      for (let i = 0; i < 8; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'fusion-bolt-trail';
          const progress = i / 8;
          const trailX = playerX + (dx * progress);
          const trailY = playerY + (dy * progress);
          trail.style.left = `${clampX(trailX, 15) - 15}px`;
          trail.style.top = `${clampY(trailY, 15) - 15}px`;
          trail.style.width = '30px';
          trail.style.height = '30px';
          trail.style.animationDelay = `${i * 40}ms`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 50);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Fusion energy discharge
      for (let i = 0; i < 12; i++) {
        const discharge = document.createElement('div');
        discharge.className = 'fusion-bolt-discharge';
        const dischargeAngle = (i / 12) * 360;
        const dischargeRadius = 40;
        const dischargeX = playerX + Math.cos(dischargeAngle * Math.PI / 180) * dischargeRadius;
        const dischargeY = playerY + Math.sin(dischargeAngle * Math.PI / 180) * dischargeRadius;
        discharge.style.left = `${clampX(dischargeX, 8) - 8}px`;
        discharge.style.top = `${clampY(dischargeY, 8) - 8}px`;
        discharge.style.animationDelay = `${i * 25}ms`;
        discharge.textContent = '💥';
        container.appendChild(discharge);
        elements.push(discharge);
      }
    }, 800);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Fusion Bolt Impact (1200-1800ms)
    const phase3Timer = setTimeout(() => {
      console.log('Fusion Bolt Phase 3: Fusion Bolt Impact');
      
      // Opponent fusion bolt hit reaction
      opponentEl.classList.add('fusion-bolt-opponent-hit');

      // Reset player position
      playerEl.classList.remove('fusion-bolt-user-launch');
      playerEl.classList.add('fusion-bolt-user-recovery');

      // Massive fusion explosion
      const fusionExplosion = document.createElement('div');
      fusionExplosion.className = 'fusion-bolt-fusion-explosion';
      fusionExplosion.style.left = `${clampX(opponentX, 150) - 150}px`;
      fusionExplosion.style.top = `${clampY(opponentY, 150) - 150}px`;
      fusionExplosion.style.width = '300px';
      fusionExplosion.style.height = '300px';
      container.appendChild(fusionExplosion);
      elements.push(fusionExplosion);

      // Fusion electrical burst particles
      for (let i = 0; i < 30; i++) {
        const burstParticle = document.createElement('div');
        burstParticle.className = 'fusion-bolt-electrical-burst';
        const burstAngle = (i / 30) * 360;
        const burstRadius = 80 + Math.random() * 70;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        burstParticle.style.left = `${clampX(burstX, 6) - 6}px`;
        burstParticle.style.top = `${clampY(burstY, 6) - 6}px`;
        burstParticle.style.animationDelay = `${i * 15}ms`;
        burstParticle.style.setProperty('--burst-x', `${Math.cos(burstAngle * Math.PI / 180) * 50}px`);
        burstParticle.style.setProperty('--burst-y', `${Math.sin(burstAngle * Math.PI / 180) * 50}px`);
        burstParticle.textContent = '⚡';
        container.appendChild(burstParticle);
        elements.push(burstParticle);
      }

      // Fusion shockwave rings
      for (let i = 0; i < 5; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'fusion-bolt-shockwave';
          shockwave.style.left = `${clampX(opponentX, 100) - 100}px`;
          shockwave.style.top = `${clampY(opponentY, 100) - 100}px`;
          shockwave.style.width = '200px';
          shockwave.style.height = '200px';
          shockwave.style.animationDelay = `${i * 80}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 100);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Fusion lightning strikes
      for (let i = 0; i < 8; i++) {
        const lightning = document.createElement('div');
        lightning.className = 'fusion-bolt-lightning-strike';
        const lightningX = opponentX + (Math.random() - 0.5) * 150;
        const lightningY = opponentY + (Math.random() - 0.5) * 120;
        lightning.style.left = `${clampX(lightningX, 5) - 5}px`;
        lightning.style.top = `${clampY(lightningY, 5) - 5}px`;
        lightning.style.animationDelay = `${i * 40}ms`;
        lightning.textContent = '🌩️';
        container.appendChild(lightning);
        elements.push(lightning);
      }
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Electrical Aftermath (1800-2200ms)
    const phase4Timer = setTimeout(() => {
      console.log('Fusion Bolt Phase 4: Electrical Aftermath');
      
      // Remove recovery effects
      playerEl.classList.remove('fusion-bolt-user-recovery');
      
      // Electrical residue effects
      for (let i = 0; i < 15; i++) {
        const residue = document.createElement('div');
        residue.className = 'fusion-bolt-electrical-residue';
        const residueX = opponentX + (Math.random() - 0.5) * 120;
        const residueY = opponentY + (Math.random() - 0.5) * 90;
        residue.style.left = `${clampX(residueX, 8) - 8}px`;
        residue.style.top = `${clampY(residueY, 8) - 8}px`;
        residue.style.animationDelay = `${i * 25}ms`;
        residue.textContent = '✨';
        container.appendChild(residue);
        elements.push(residue);
      }
      
      // Fusion energy dissipation removed to prevent orb appearing at screen edges
    }, 1800);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // Helper to remove any stray nodes that might have been left behind
    const cleanupStrays = () => {
      const straySelectors = [
        '.fusion-bolt-fusion-core',
        '.fusion-bolt-fusion-particle',
        '.fusion-bolt-fusion-ring',
        '.fusion-bolt-projectile',
        '.fusion-bolt-trail',
        '.fusion-bolt-discharge',
        '.fusion-bolt-fusion-explosion',
        '.fusion-bolt-electrical-burst',
        '.fusion-bolt-shockwave',
        '.fusion-bolt-lightning-strike',
        '.fusion-bolt-electrical-residue',
        '.fusion-bolt-fusion-dissipation',
      ].join(',');
      document.querySelectorAll(straySelectors).forEach(el => {
        if (el && el.parentNode) el.remove();
      });
    };

    // CLEANUP (2200ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Fusion Bolt: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('fusion-bolt-user-charging', 'fusion-bolt-user-launch', 'fusion-bolt-user-recovery');
      opponentEl.classList.remove('fusion-bolt-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Just in case: remove any stray nodes
      cleanupStrays();

      // Animation complete
      onComplete();
    }, 2200);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('fusion-bolt-user-charging', 'fusion-bolt-user-launch', 'fusion-bolt-user-recovery');
      opponentEl.classList.remove('fusion-bolt-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      cleanupStrays();
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
FusionBoltAnimation.moveType = 'electric';
